/**
 * MobileEgo (contracts/ego.md): the realdata session → episode → label API, its staff review side, and the platform
 * taxonomy calls labelling needs. Types follow the contract; the small mapping helpers below turn wire shapes into
 * what screens need (capture time ↔ proxy time, the contributor's status words, synonyms, media paths) and are
 * unit-tested against the contract's examples, so a screen never re-derives them.
 */
import type { ApiClient } from './client.ts'
import type { AgentDataDay, AgentDataSettings, AgentDataStatus } from './types.ts'
import { ApiError } from './errors.ts'

const enc = encodeURIComponent

// ── Units and time (§1) ──────────────────────────────────────────────────────────────────────────────────────

/** One maximal run of kept segments: capture time (src, removed parts included) → proxy/export time (out). */
export interface RetainedRun {
  from_i: number
  to_i: number
  src_from_ms: number
  src_to_ms: number
  out_from_ms: number
  out_to_ms: number
}

export interface Span {
  start_ms: number
  end_ms: number
}

/** Segment length (CIP `segment_seconds`): removals snap outward to it. */
export const SEGMENT_MS = 2000

/** The proxy time of capture time `t`, or null when `t` lies in a removed gap. */
export function toOutputMs(runs: readonly RetainedRun[], t: number): number | null {
  for (const r of runs) if (t >= r.src_from_ms && t <= r.src_to_ms) return r.out_from_ms + (t - r.src_from_ms)
  return null
}

/** The capture time of proxy time `out` (the end of the last run past the end). */
export function toCaptureMs(runs: readonly RetainedRun[], out: number): number {
  if (!runs.length) return out
  for (const r of runs) if (out >= r.out_from_ms && out < r.out_to_ms) return r.src_from_ms + (out - r.out_from_ms)
  const last = runs[runs.length - 1]!
  return out <= runs[0]!.out_from_ms ? runs[0]!.src_from_ms : last.src_to_ms
}

/** Capture time moved out of a removed gap: to the next kept run (or the end of the last one). */
export function snapToRetained(runs: readonly RetainedRun[], t: number): number {
  if (!runs.length || toOutputMs(runs, t) !== null) return t
  const next = runs.find((r) => r.src_from_ms > t)
  return next ? next.src_from_ms : runs[runs.length - 1]!.src_to_ms
}

/** The removed gaps of a recording `recordedMs` long, in capture time. */
export function removedSpans(runs: readonly RetainedRun[], recordedMs: number): Span[] {
  const out: Span[] = []
  let at = 0
  for (const r of [...runs].sort((a, b) => a.src_from_ms - b.src_from_ms)) {
    if (r.src_from_ms > at) out.push({ start_ms: at, end_ms: r.src_from_ms })
    at = Math.max(at, r.src_to_ms)
  }
  if (recordedMs > at) out.push({ start_ms: at, end_ms: recordedMs })
  return out
}

/** Retained runs rebuilt from removed spans (the staff item gives spans): kept runs concatenated in order. */
export function runsFromRemoved(removed: readonly Span[], recordedMs: number): RetainedRun[] {
  const gaps = [...removed].sort((a, b) => a.start_ms - b.start_ms)
  const runs: RetainedRun[] = []
  let at = 0
  let out = 0
  const push = (from: number, to: number) => {
    if (to <= from) return
    runs.push({ from_i: Math.floor(from / SEGMENT_MS), to_i: Math.max(Math.floor(from / SEGMENT_MS), Math.ceil(to / SEGMENT_MS) - 1), src_from_ms: from, src_to_ms: to, out_from_ms: out, out_to_ms: out + (to - from) })
    out += to - from
  }
  for (const g of gaps) {
    push(at, Math.min(g.start_ms, recordedMs))
    at = Math.max(at, g.end_ms)
  }
  push(at, recordedMs)
  return runs
}

/** How much of [start, end) is retained. */
export function retainedMsIn(runs: readonly RetainedRun[], start: number, end: number): number {
  let ms = 0
  for (const r of runs) ms += Math.max(0, Math.min(end, r.src_to_ms) - Math.max(start, r.src_from_ms))
  return ms
}

/** Total proxy length. */
export function outputLength(runs: readonly RetainedRun[]): number {
  return runs.reduce((m, r) => Math.max(m, r.out_to_ms), 0)
}

// ── Missions (§2) ────────────────────────────────────────────────────────────────────────────────────────────

export type EgoMissionKind = 'open' | 'campaign' | 'practice'
export type EgoTier = 'rgb' | 'spatial' | 'depth'

export interface CampaignRequirements {
  environments: string[]
  tasks: string[]
  actions: string[]
  objects: string[]
  min_quality?: number | null
  tier?: EgoTier | null
  min_useful_minutes?: number | null
}

export interface CampaignTargets {
  episodes?: number | null
  useful_hours?: number | null
  contributors?: number | null
  environments?: number | null
}

/** The staff-set campaign fields of a realdata mission (`kind: campaign`). */
export interface CampaignFields {
  kind: EgoMissionKind
  requirements: CampaignRequirements
  targets: CampaignTargets
  budget_points: number | null
  demand: number
  customer_org_id: string | null
  accept_matches: boolean
  match_days: number
  criteria: string
}

/** `GET /v1/admin/missions/{id}` (realdata) as far as the campaign editor reads it. */
export interface RealdataAdminMission extends Partial<CampaignFields> {
  id: string
  version: number
  title: string
  status?: string
  base_points?: number
  platform_mission_id?: string | null
  closes_at?: string | null
  /** A proposed change to what the mission pays, waiting for a second person. */
  pending_reward?: Record<string, unknown> | null
  [extra: string]: unknown
}

/** The §2 fields a campaign save sends, with the audited reason. */
export type CampaignPatch = Partial<Omit<CampaignFields, 'kind'>> & { reason: string }

/** `GET /v1/admin/missions/{id}/progress` (plan §15.3). */
export interface CampaignProgress {
  requested: CampaignTargets
  submitted: number
  accepted_useful_ms: number
  contributors: number
  environments: number
  coverage: { key: string; episodes: number }[]
  budget_left: number | null
  quality: { p25: number | null; p50: number | null; p75: number | null }
}

/** What a campaign has collected so far (`GET /v1/missions` items, contributor view, §2). */
export interface EgoCampaignTally {
  episodes: number
  useful_hours: number
  useful_ms?: number
  contributors: number
  environments: number
  budget_left: number | null
  targets_met?: boolean
}

/** `GET /v1/missions` (bearer): the active realdata missions as a contributor reads them; campaigns carry their terms
 * and progress (contracts/ego.md §2). */
export interface EgoMissionSpec {
  id: string
  version?: number
  domain_ref: string
  platform_mission_id: string | null
  title: string
  summary?: string | null
  kind?: EgoMissionKind
  practice?: boolean
  base_points?: number
  est_minutes?: number | null
  requirements?: Partial<CampaignRequirements> | null
  targets?: CampaignTargets | null
  demand?: number | null
  criteria?: string | null
  closes_at?: string | null
  progress?: EgoCampaignTally | null
}

export interface CampaignMatchBody {
  environment: string | null
  labels: string[]
  tier: EgoTier
  recorded_at: string
}

export interface CampaignMatch {
  mission_id: string
  platform_mission_id: string | null
  title: string
  demand: number
  fits: boolean
  missing: string[]
}

// ── Annotation (§5) ──────────────────────────────────────────────────────────────────────────────────────────

/** `environment` labels only ever name a proposal: a known environment is the episode's `environment` field. */
export type LabelRole = 'activity' | 'subtask' | 'action' | 'object' | 'environment'
export type ProposalKind = 'task' | 'skill' | 'object_category' | 'environment'

/** Roles → skill kinds (§5); the environment is an episode field. */
export const ROLE_KIND: Record<LabelRole | 'environment', ProposalKind> = {
  activity: 'task',
  subtask: 'task',
  action: 'skill',
  object: 'object_category',
  environment: 'environment',
}

export interface EpisodeLabel {
  id: string
  role: LabelRole
  /** An active platform skill key, or… */
  key?: string
  /** …an id in the document's `proposals`. Exactly one of the two. */
  proposal?: string
  /** Bookmark ids. */
  evidence?: string[]
}

export interface Episode {
  id: string
  start_ms: number
  end_ms: number
  title?: string
  environment?: string | null
  campaign_id?: string | null
  labels: EpisodeLabel[]
}

export interface EvidenceBookmark {
  id: string
  t_ms: number
  /** 0 for a frame; a clip up to 10 s. */
  dur_ms: number
  note?: string
}

export interface LabelProposalDraft {
  id: string
  kind: ProposalKind
  name: string
  parent_key?: string | null
  explanation?: string
  evidence: string[]
}

/** How the owner turned the player: clockwise degrees on top of the stored upright frame (contracts/ego.md §5). */
export type ViewRotation = 0 | 90 | 180 | 270

export interface AnnotationDoc {
  v: 1
  /** Absent in documents from before the November 2026 editor: 0. */
  view_rotation?: ViewRotation
  episodes: Episode[]
  bookmarks: EvidenceBookmark[]
  proposals: LabelProposalDraft[]
}

export interface AnnotationState {
  doc: AnnotationDoc
  version: number
  editable: boolean
  updated_at: string | null
  updated_from: 'phone' | 'web' | null
}

export interface AnnotationWarning {
  code: 'duplicate_label' | string
  label?: string
  [extra: string]: unknown
}

export interface AnnotationSaved {
  version: number
  doc: AnnotationDoc
  warnings: AnnotationWarning[]
}

export const ANNOTATION_LIMITS = { episodes: 50, labelsPerEpisode: 60, bookmarks: 200, proposals: 20, minEpisodeMs: 5000, minRetainedMs: 10_000, maxClipMs: 10_000 } as const

/** Ids are client-chosen and stable across edits. */
export const ANNOTATION_ID = /^[a-z]{2}_[A-Za-z0-9_-]{1,40}$/

/** "No annotation" (older app builds, or before the first save): one episode over the retained recording, no labels. */
export function defaultAnnotation(runs: readonly RetainedRun[], recordedMs: number): AnnotationDoc {
  const start = runs.length ? Math.min(...runs.map((r) => r.src_from_ms)) : 0
  const end = runs.length ? Math.max(...runs.map((r) => r.src_to_ms)) : recordedMs
  return { v: 1, episodes: [{ id: 'ep_1', start_ms: start, end_ms: end, title: '', environment: null, campaign_id: null, labels: [] }], bookmarks: [], proposals: [] }
}

// ── Vocabulary (§6) ──────────────────────────────────────────────────────────────────────────────────────────

export interface VocabNode {
  key: string
  kind: ProposalKind | string
  name: string
  parent_key: string | null
  synonyms: string[]
  involves: string[]
  in: string[]
  requires: string[]
  status: 'active' | string
}

export interface VocabBundle {
  version: string
  locale?: string
  kinds?: string[]
  nodes: VocabNode[]
}

export interface SkillSearchHit {
  key: string
  kind: ProposalKind | string
  name: string
  parent_key?: string | null
  matched: 'name' | 'synonym' | 'alias'
  text: string
  score: number
  status: string
}

/** A synonym as the taxonomy stores it (per locale). */
export interface Synonym {
  text: string
  locale: string
  /** Who added it (staff, a merged node, a seed…); read-only. */
  source?: string
}

/** Synonyms arrive as strings (bundle, in the requested locale) or `{text, locale, source}` (node detail): one shape. */
export function normalizeSynonyms(raw: unknown, locale = 'en'): Synonym[] {
  if (!Array.isArray(raw)) return []
  const out: Synonym[] = []
  const seen = new Set<string>()
  for (const s of raw) {
    const o = s && typeof s === 'object' ? (s as Partial<Synonym>) : null
    const syn: Synonym | null = typeof s === 'string' ? { text: s, locale } : o && typeof o.text === 'string' ? { text: o.text, locale: String(o.locale ?? locale), ...(o.source ? { source: String(o.source) } : {}) } : null
    if (!syn || !syn.text.trim()) continue
    const k = `${syn.locale}\u0000${syn.text.trim().toLowerCase()}`
    if (seen.has(k)) continue
    seen.add(k)
    out.push({ ...syn, text: syn.text.trim() })
  }
  return out
}

/**
 * The `synonyms` of a node update: `{locale: [text…]}` replaces each given locale, so every locale the node had or
 * has now is sent (an emptied locale as `[]`) and nothing else changes behind the editor's back.
 */
export function synonymsPatch(before: readonly Synonym[], after: readonly Synonym[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const s of before) out[s.locale] ??= []
  for (const s of after) (out[s.locale] ??= []).push(s.text)
  return out
}

/** Taxonomy fields of a node (§12) the ops skills page edits. */
export interface SkillTaxonomy {
  synonyms: Synonym[]
  names: Record<string, string>
  merged_into: string | null
}

/** Translations exclude English, which is the node's `name` itself (the platform refuses `en` in `names`). */
export function taxonomyOf(node: { synonyms?: unknown; names?: unknown; merged_into?: unknown } | null | undefined): SkillTaxonomy {
  const names = node?.names && typeof node.names === 'object' ? Object.fromEntries(Object.entries(node.names as Record<string, unknown>).filter(([k, v]) => k !== 'en' && typeof v === 'string' && v.trim()).map(([k, v]) => [k, String(v).trim()])) : {}
  return { synonyms: normalizeSynonyms(node?.synonyms), names, merged_into: typeof node?.merged_into === 'string' ? node.merged_into : null }
}

export interface SkillMergeResult {
  key: string
  name: string
  merged?: Record<string, number | string | null>
  [extra: string]: unknown
}

export interface SkillTaxonomyPatch {
  /** `{locale: [text…]}` replaces each given locale (see synonymsPatch). */
  synonyms?: Record<string, string[]>
  /** Replaces the whole map; never `en` (English is `name`). */
  names?: Record<string, string>
  status?: 'active' | 'draft' | 'deprecated'
  reason?: string
}

// ── Review states and the contributor's view (§7, §8) ───────────────────────────────────────────────────────

export type EgoInternalStatus = 'queued' | 'validating' | 'needs_review' | 'needs_correction' | 'accepted' | 'partially_accepted' | 'rejected' | 'practice_done' | 'withdrawn'
export type EgoContributorStatus = 'unsubmitted' | 'checking' | 'in_review' | 'needs_correction' | 'accepted' | 'partially_accepted' | 'rejected' | 'practice_done' | 'withdrawn'

/** §7: what the contributor sees for an internal submission status (already-mapped values pass through). */
export function contributorStatus(s: string): EgoContributorStatus {
  if (s === 'queued' || s === 'validating') return 'checking'
  if (s === 'needs_review') return 'in_review'
  return s as EgoContributorStatus
}

export interface PublicReason {
  code: string
  title: string
  body: string
  tip?: string
}

export interface EgoScoreEpisode {
  id: string
  title: string
  decision: 'accepted' | 'rejected' | 'pending'
  useful_ms: number
  /** `verified: false` while the place isn't confirmed (a proposed or unchecked environment): the rate stays 1.0. */
  domain: { key: string | null; rate: number; verified?: boolean }
  quality: { tier: string; multiplier: number }
  novelty: { multiplier: number; note?: string | null }
  demand: { multiplier: number; campaign_id: string | null }
  points: number
}

export interface EgoScoreLabel {
  key: string
  name: string
  role: LabelRole | 'environment'
  points?: number
  reason?: string
}

/** `realdata.ego@1`, contributor-safe: no fraud thresholds or risk values (§8). */
export interface EgoScore {
  policy: string
  stage: 'preliminary' | 'final'
  duration: { recorded_ms: number; kept_ms: number; removed_by_you_ms: number; idle_ms: number; masked_ms: number; rejected_ms: number; useful_ms: number }
  episodes: EgoScoreEpisode[]
  capture_points: number
  labels: { approved: EgoScoreLabel[]; rejected: EgoScoreLabel[]; pending: EgoScoreLabel[]; points: number; capped: boolean }
  spatial: { pose: boolean; depth: boolean; points: number }
  bonuses: { kind: 'new_category' | string; proposal_id: string; points: number; status: 'pending' | 'granted' | 'none' }[]
  profile: { standard: boolean; reasons: string[] }
  daily_cap_applied: boolean
  total: number
  /** Server-written score lines (resilience.md §3): render these when present, else today's layout. */
  lines?: { key: string; label: string; value: number }[] | null
  /** Server-written words for the total ("38 points"). */
  total_label?: string | null
}

/** Links to a capture's media (§10); null until the proxy job ran. Relative, with or without the gateway prefix. */
export interface EgoMediaLinks {
  proxy?: string | null
  thumbs?: string | null
  thumbs_json?: string | null
  bookmarks?: Record<string, { jpg?: string | null; mp4?: string | null }>
  [extra: string]: unknown
}

export interface ThumbsMeta {
  tile_w: number
  tile_h: number
  cols: number
  every_ms: number
  count: number
}

/** `GET /v1/me/captures` item (summary fields, no score). */
export interface MyCapture {
  id: string
  capture_id: string
  mission_id: string
  mission_title: string
  /** The first titled episode's title, else null; shown before `mission_title` (§8). */
  title?: string | null
  kind?: EgoMissionKind
  status: EgoContributorStatus | string
  practice?: boolean
  points: number
  estimate_points?: number | null
  reasons: PublicReason[]
  duration_ms?: number | null
  kept_ms?: number | null
  /** Accepted, useful time once decided (when the service sends it). */
  useful_ms?: number | null
  size_bytes?: number | null
  tier?: EgoTier | null
  truncated?: boolean
  episodes?: number
  due_at?: string | null
  annotation_editable?: boolean
  corrections?: string[]
  created_at: string | null
  decided_at: string | null
  recorded_at?: string | null
  // ── Recording lifecycle (docs/redesign-2026-11/lifecycle.md); optional: older servers don't send them ──
  /** Where the recording is: the statuses both apps show. */
  phase?: RecordingPhase | string
  /** "Main activity · Place" of the first labelled part, a request's title before labels; null: the app shows the
   * date. */
  name?: string | null
  /** Distinct labels: places first (`role: 'place'`), then main activities (`activity`), then the rest. */
  labels?: RecordingLabel[]
  /** Parts, and parts with a place and a main activity. */
  parts_total?: number
  parts_labelled?: number
  /** The review attempt: 1, +1 per Send again. */
  attempt?: number
  /** While `labels_rejected`: how many reviewer notes (corrections + rejected labels). */
  notes?: number
  /** What the owner may do now. */
  can_edit?: boolean
  can_finish?: boolean
  /** What the submit check still misses (empty when Finish may go). */
  missing?: MissingLabels[]
  can_delete?: boolean
  /** The owner can watch it now and the playable copy is ready. */
  video?: boolean
  /** Sent by an app that finishes explicitly. */
  explicit_finish?: boolean
  /** The reviewer's own note on the latest "labels not accepted" or "not accepted"; null otherwise. */
  review_note?: string | null
  /** While `labels_rejected`: the labels the reviewer didn't accept (`id` is the annotation label id). */
  rejected_labels?: RejectedLabel[]
}

/** A label a reviewer didn't accept: `unsupported` (not shown in the video), `wrong_kind`, `duplicate`. */
export interface RejectedLabel {
  id: string
  episode_id: string
  role: string
  name: string | null
  reason: 'unsupported' | 'wrong_kind' | 'duplicate' | string | null
}

/** `phase` values (lifecycle.md); `practice` is older practice runs, `unsubmitted` one not sent yet. */
export type RecordingPhase = 'needs_labels' | 'in_review' | 'labels_rejected' | 'scored' | 'not_accepted' | 'practice' | 'unsubmitted'

export interface RecordingLabel {
  key: string | null
  name: string
  /** `place`, `activity`, or the label's role (`object`, `action`, …). */
  role: string
}

/** The submit check's answer for one part: `episode` null when there is no part at all. `needs`: `environment`,
 * `environment_evidence`, `activity`, `activity_evidence` (or `episode`). */
export interface MissingLabels {
  episode: string | null
  needs: string[]
}

/** `POST /v1/me/captures/{id}/finish`. */
export interface FinishResult {
  capture_id: string
  phase: RecordingPhase | string
}

/** `POST /v1/me/captures/{id}/delete`. */
export interface DeleteResult {
  capture_id: string
  deleted: boolean
}

/** `GET /v1/me/captures/{id}`. */
export interface MyCaptureDetail extends MyCapture {
  score?: EgoScore | null
  /** The labelling rules this capture is checked against (resilience.md §3); today's when missing. */
  annotation_rules?: unknown
  retained?: RetainedRun[]
  media?: EgoMediaLinks | null
  timeline?: { state: string; at: string | null }[]
  can_appeal?: boolean
  /** A second look the owner asked for, and its answer. */
  appeal?: { status: string; note?: string | null } | null
}

export interface MyCapturesPage {
  items: MyCapture[]
  next: string | null
  unsubmitted?: { id: string; capture_id: string; mission_id: string; status: 'unsubmitted'; segments: number; recorded_at: string | null }[]
}

export interface EgoSummary {
  submitted_ms: number
  accepted_useful_ms: number
  captures: number
  episodes: number
  tasks: number
  verified_labels: number
  diversity: { environments: number; activities: number; objects: number; action_object_pairs: number }
  points: { preliminary: number; final: number }
  pending_reviews: number
  next_due_at: string | null
  corrections_available: number
}

// ── Staff (§9) ───────────────────────────────────────────────────────────────────────────────────────────────

export interface ReviewQueueItem {
  id: string
  capture_id: string
  mission_id: string
  user_id: string
  status: string
  practice: boolean
  risk: number | null
  quality: number | null
  points: number
  estimate_points?: number | null
  reasons: string[]
  created_at: string | null
  due_at: string | null
  overdue: boolean
  priority: number
  audit: boolean
  episodes: number
  proposals: number
  kind?: 'capture' | 'correction'
  trust_level?: number | null
  mission_title?: string | null
  duration_ms?: number | null
}

export type AutoDecision = 'approved' | 'rejected' | null

export interface ReviewLabel extends EpisodeLabel {
  name?: string
  /** Label verification: how strongly the frames support it (0–1) and how sure that is. */
  support?: number | null
  confidence?: number | null
  /** The automatic suggestion, if verification made one. */
  auto?: AutoDecision
  decision?: 'approved' | 'rejected' | 'pending' | null
}

/** An episode's place as the reviewer sees it: the key, and CLIP's support for it (like a label's). */
export interface ReviewEnvironment {
  key: string | null
  name?: string | null
  support?: number | null
  confidence?: number | null
  auto?: AutoDecision
}

export interface ReviewEpisode extends Omit<Episode, 'labels' | 'environment'> {
  environment: ReviewEnvironment | null
  labels: ReviewLabel[]
  useful_ms?: number | null
  idle_ms?: number | null
  masked_ms?: number | null
  decision?: 'accepted' | 'rejected' | 'pending' | null
}

export interface ReviewSpans {
  removed: Span[]
  idle: Span[]
  masked: Span[]
  paused: Span[]
  flags: (Span & { code?: string; label?: string })[]
}

export interface DuplicateMatch {
  capture_id: string
  similarity?: number | null
  segments?: number[]
  user_id?: string | null
}

/** What the phone measured while recording, sent with the upload (lifecycle.md, submit `summary`): fractions 0–1 of
 * the kept video; spans `[from_ms, to_ms]` of the recording, at most 50 per kind. */
export interface PhoneSummary {
  hands_any?: number | null
  hands_both?: number | null
  dark?: number | null
  bright?: number | null
  moving?: number | null
  face?: number | null
  dropped?: number | null
  samples?: number | null
  spans?: { dark?: [number, number][]; no_hands?: [number, number][]; face?: [number, number][] } | null
  /** How the phone's own checks ran (rt_realdata.capture.service.clean_health); absent from older app builds. */
  health?: PhoneHealth | null
}

/** Whether the phone's hand model loaded (no hands seen may be the phone's fault), what it analysed, its camera. */
export interface PhoneHealth {
  hand_model_loaded?: boolean
  /** The engine's error code when the hand model didn't load (e.g. hands_unavailable). */
  hand_model_error?: string
  /** Quality samples the phone measured (~4 a second) over the whole recording, and those with a hand in view. */
  frames_analysed?: number
  frames_with_hands?: number
  /** Mean brightness, 0–1. */
  brightness?: number
  camera_path?: 'arcore' | 'camera2' | 'arkit' | 'preview'
  preview_rotation?: 0 | 90 | 180 | 270
}

/** The reviewer's facts (lifecycle.md, light checks): none of these decide anything. */
export interface ReviewFacts {
  /** The signed chain verified at submit: true, false, or null when it wasn't checked. */
  signed: boolean | null
  device_trust?: number | null
  platform?: string | null
  /** Parts kept, of all the parts recorded. */
  parts: number
  parts_total: number
  /** Exact repeats of earlier footage. */
  same_footage: DuplicateMatch[]
  phone: PhoneSummary | null
  attempt: number
  /** Episode ids changed since the labels were last not accepted. */
  changed_parts: string[]
  contributor: { accepted: number; rejected: number } | null
}

/** `GET /v1/review/items/{capture_id}`: the existing record plus the ego additions. */
export interface ReviewItem {
  /** Lifecycle facts (servers from November 2026 on). */
  facts?: ReviewFacts | null
  submission: { id: string; capture_id: string; mission_id: string; user_id: string; status: string; risk: number | null; quality: number | null; points: number; reasons: string[]; review_reasons?: string[]; created_at: string | null; decided_at?: string | null; trust_level?: number | null; [k: string]: unknown }
  capture: { id: string; mount?: string; segments?: number; excluded?: number[]; bytes?: number; recorded_at?: string | null; labels?: { pose?: string | null; depth?: string | null; imu?: string | null }; [k: string]: unknown }
  device?: { id: string; platform?: string; model?: string; os?: string; trust_level?: number; app_build?: number; [k: string]: unknown }
  validation?: { version?: string; checks: Record<string, unknown>; risk?: number | null; duration_s?: number | null } | null
  challenges?: { id: string; kind: string; result?: string | null; detail?: unknown; window_ms?: number; sent_ms?: number }[]
  reviews?: { reviewer_id: string; decision: string; reason_code: string | null; note?: string | null; created_at: string | null }[]
  episodes: ReviewEpisode[]
  bookmarks: EvidenceBookmark[]
  proposals: LabelProposalDraft[]
  profile?: { standard: boolean; reasons?: string[]; measured?: { width?: number; height?: number; fps?: number; bitrate_mbps?: number; codec?: string; dropped_pct?: number } } | null
  spans: ReviewSpans
  duplicates: { exact: DuplicateMatch[]; near: DuplicateMatch[] }
  /** The full breakdown, internals included (staff only). */
  score?: (EgoScore & Record<string, unknown>) | null
  retained?: RetainedRun[]
  media?: EgoMediaLinks | null
  due_at?: string | null
  annotation_versions?: { version: number; at: string | null; by?: string | null; author?: string | null; surface?: 'phone' | 'web' | string }[]
  /** The annotation version this view shows; a decision names it, so edits made meanwhile aren't decided blind. */
  annotation_version: number
  /** The owner's player turn (the latest version's `view_rotation`); 0 or absent: as stored. */
  view_rotation?: ViewRotation
}

export type SubmissionDecision = 'accept' | 'partial' | 'needs_correction' | 'reject' | 'escalate'
export type LabelReason = 'unsupported' | 'wrong_kind' | 'duplicate' | 'fabricated'
export type CorrectionCode = 'labels_unsupported' | 'episode_boundaries' | 'missing_evidence' | string

export interface ReviewDecisionBody {
  decision: SubmissionDecision
  reason: string | null
  note: string
  episodes: { id: string; decision: 'accept' | 'reject'; accepted_ms: number; reason: string | null; environment?: EnvironmentDecision }[]
  labels: { id: string; decision: 'approve' | 'reject' | 'relabel'; key?: string; reason: LabelReason | null }[]
  corrections: CorrectionCode[]
  /** The version reviewed (409 `annotation_changed` when the contributor saved a newer one meanwhile). */
  annotation_version: number
}

/** The reviewer's call on an episode's place; left out, the automatic suggestion stands. */
export interface EnvironmentDecision {
  decision: 'approve' | 'reject' | 'relabel'
  key?: string
}

export interface ReviewDecisionResult {
  id: string
  status: string
  next?: ReviewItem | null
  [k: string]: unknown
}

export type ProposalStatus = 'pending' | 'needs_evidence' | 'decided'

export interface ProposalEvidence {
  id: string
  capture_id: string
  t_ms: number
  dur_ms: number
  note?: string
  /** Media links (relative): a JPEG for every bookmark, an MP4 for a clip. */
  image?: string | null
  clip?: string | null
}

export interface LabelProposal {
  id: string
  kind: ProposalKind
  name: string
  parent_key: string | null
  explanation: string
  status: ProposalStatus | string
  user_id: string
  capture_id: string
  created_at: string | null
  decision?: string | null
  key?: string | null
  evidence_count?: number
}

export interface ProposalDetail extends LabelProposal {
  evidence: ProposalEvidence[]
  similar: { key: string; name: string; kind: string; score: number }[]
}

export type ProposalDecision = 'approve_existing' | 'approve_new' | 'merge' | 'reject' | 'needs_evidence'

export interface ProposalDecisionBody {
  decision: ProposalDecision
  key?: string
  name?: string
  parent_key?: string | null
  description?: string
  synonyms?: string[]
  note: string
}

export interface LabelCoverageRow {
  key: string
  episodes: number
  useful_ms: number
  contributors: number
  environments: number
  target: number | null
  met: boolean
}

export interface LabelCoverage {
  items: LabelCoverageRow[]
  pairs: { action: string; object: string; episodes: number }[]
}

export interface CapturePolicy {
  masking: boolean
  max_area_permille: number
  deadlines_h: { checking: number; labels: number; final: number }
  audit_rate: number
  open_max_seconds: number
  updated_at?: string | null
  updated_by?: string | null
}

export interface ContributorRealdata {
  trust_score: number | null
  label_accuracy: number | null
  risk_score: number | null
  accepted: number
  rejected: number
  reviews: { capture_id: string; decision: string; at: string | null }[]
  proposals: { approved: number; rejected: number }
}

/** `auto` arrives as approve/approved/true or reject/rejected; anything else is no suggestion. */
export function autoOf(v: unknown): AutoDecision {
  if (v === true || v === 'approve' || v === 'approved') return 'approved'
  if (v === 'reject' || v === 'rejected') return 'rejected'
  return null
}

/** A capture-time range as `[from, to]`, `{start_ms, end_ms}` or `{from_ms, to_ms, kind}`. */
export function spanOf(v: unknown): (Span & { code?: string; label?: string }) | null {
  if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number') return { start_ms: v[0], end_ms: v[1] }
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const a = (o.start_ms ?? o.from_ms) as number | undefined
  const b = (o.end_ms ?? o.to_ms) as number | undefined
  if (typeof a !== 'number' || typeof b !== 'number') return null
  const code = (o.code ?? o.kind) as string | undefined
  return { start_ms: a, end_ms: b, ...(code ? { code } : {}), ...(typeof o.label === 'string' ? { label: o.label } : {}) }
}

const spansOf = (v: unknown) => (Array.isArray(v) ? v.map(spanOf).filter((x): x is NonNullable<typeof x> => !!x) : [])

/**
 * The review item as the screens read it, from what the service sends: an episode's environment as an object
 * (`{key, name, support, confidence, auto}`, or a bare key), labels naming their proposal as `proposal_id`, spans as
 * pairs, media under `media_links`, and the reviewed annotation version (`annotation_version`, else the latest of
 * `annotation_versions`).
 */
export function normalizeReviewItem(raw: Record<string, unknown>): ReviewItem {
  const r = raw as Record<string, unknown> & Partial<ReviewItem>
  const episodes = ((r.episodes ?? []) as unknown as Record<string, unknown>[]).map((e) => {
    const env = e.environment
    const environment: ReviewEnvironment | null =
      typeof env === 'string' ? { key: env } : env && typeof env === 'object' ? { ...(env as ReviewEnvironment), key: ((env as ReviewEnvironment).key ?? null), auto: autoOf((env as ReviewEnvironment).auto) } : null
    const labels = ((e.labels ?? []) as Record<string, unknown>[]).map((l) => {
      const { proposal_id, ...rest } = l
      return { ...(rest as unknown as ReviewLabel), ...(l.key == null ? {} : { key: l.key as string }), ...(proposal_id || l.proposal ? { proposal: (l.proposal ?? proposal_id) as string } : {}), auto: autoOf(l.auto) } as ReviewLabel
    })
    return { ...(e as unknown as ReviewEpisode), environment: environment && (environment.key || environment.name || environment.support != null) ? environment : null, labels }
  })
  const sp = (r.spans ?? {}) as Record<string, unknown>
  const versions = (r.annotation_versions ?? []) as NonNullable<ReviewItem['annotation_versions']>
  const latest = versions.reduce((m, v) => Math.max(m, Number(v.version) || 0), 0)
  return {
    ...(r as unknown as ReviewItem),
    episodes,
    bookmarks: (r.bookmarks ?? []) as ReviewItem['bookmarks'],
    proposals: ((r.proposals ?? []) as LabelProposalDraft[]).map((p) => ({ ...p, evidence: p.evidence ?? [] })),
    spans: { removed: spansOf(sp.removed), idle: spansOf(sp.idle), masked: spansOf(sp.masked), paused: spansOf(sp.paused), flags: spansOf(sp.flags) },
    duplicates: { exact: (r.duplicates?.exact ?? []) as DuplicateMatch[], near: (r.duplicates?.near ?? []) as DuplicateMatch[] },
    media: (r.media ?? (r.media_links as EgoMediaLinks | undefined) ?? null) as EgoMediaLinks | null,
    annotation_versions: versions,
    annotation_version: typeof r.annotation_version === 'number' ? r.annotation_version : latest,
  }
}

// ── Media (§10) ──────────────────────────────────────────────────────────────────────────────────────────────

export type MediaFile = 'proxy.mp4' | 'thumbs.jpg' | 'thumbs.json' | `bookmarks/${string}.jpg` | `bookmarks/${string}.mp4`

/** The service path (under /api/realdata) of an owner's or a reviewer's media file. */
export function mediaPath(scope: 'owner' | 'staff', captureId: string, file: MediaFile): string {
  return scope === 'owner' ? `/v1/me/captures/${enc(captureId)}/media/${file}` : `/v1/review/items/${enc(captureId)}/media/${file}`
}

/** A media link as the service sent it, as a path under /api/realdata (null for anything else). */
export function linkPath(link: string | null | undefined): string | null {
  if (!link) return null
  const m = link.match(/^(?:https?:\/\/[^/]+)?(?:\/api\/realdata)?(\/v1\/.+)$/)
  return m ? m[1]! : null
}

// ── Ego library (ops): staff-owned footage from R2 buckets, Hugging Face datasets and local folders, edited into
// episodes for the ego agents ──────────────────────────────────────────────────────────────────────────────────────

export type LibraryVideoStatus = 'new' | 'processing' | 'ready' | 'failed'
export type LibraryReviewStatus = 'unreviewed' | 'usable' | 'rejected'
export type LibraryPrivacyStatus = 'unchecked' | 'running' | 'review' | 'checked' | 'failed'
export type LibrarySegmentStatus = 'draft' | 'usable' | 'rejected'
export type LibrarySegmentState = 'available' | 'planned' | 'submitted' | 'accepted' | 'unusable'
export type LibraryMediaName = 'proxy.mp4' | 'thumbs.jpg' | 'thumbs.json' | 'blurred.mp4'

export type LibrarySourceKind = 'r2' | 'hf' | 'local'

/** Public config of a source. Secrets are never here: R2 keys come from a credentials profile in
 * var/env/realdata.env (RT_REALDATA_LIBRARY_<PROFILE>_ACCESS_KEY_ID / _SECRET_ACCESS_KEY), an HF token from the env
 * setting named by `token_env`. */
export type LibrarySourceConfig =
  | { bucket: string; prefix: string; credentials: string; endpoint?: string; account_id?: string }
  | { repo_id: string; revision: string; path_prefix: string; repo_type: 'dataset' | 'model'; token_env: string }
  | { path: string }

export interface LibrarySource {
  id: string
  name: string
  kind: LibrarySourceKind
  config: LibrarySourceConfig & Record<string, unknown>
  enabled: boolean
  videos: number
  last_import_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface LibrarySourceCheck {
  ok: boolean
  detail: string
  sample: string[]
}

export interface LibraryImport {
  id: string
  source_id: string | null
  status: 'running' | 'done' | 'failed'
  prefix: string
  found: number
  added: number
  error: string | null
  started_at: string
  finished_at: string | null
}

export interface LibraryVideoSummary {
  id: string
  /** The file's key inside its source (object key, repo path or path under the folder). */
  key: string
  source: { id: string; name: string; kind: LibrarySourceKind } | null
  title: string
  duration_ms: number | null
  width: number | null
  height: number | null
  fps: number | null
  size_bytes: number | null
  recorded_at: string | null
  status: LibraryVideoStatus
  error: string | null
  review_status: LibraryReviewStatus
  privacy_status: LibraryPrivacyStatus
  segments: number
  labeled: number
  /** Points per minute for this video's episodes; null = the contributors' rate (`default_rate_per_min`). */
  rate_per_min: number | null
  default_rate_per_min: number
  points_total: number
  points_used: number
  notes: string | null
  updated_at: string
}

export interface LibraryLabel {
  role: 'activity' | 'subtask' | 'action' | 'object'
  key: string
  start_ms: number | null
  end_ms: number | null
}

export interface LibrarySegment {
  id: string
  video_id: string
  start_ms: number
  end_ms: number
  duration_ms: number
  title: string
  environment: string | null
  status: LibrarySegmentStatus
  notes: string | null
  labels: LibraryLabel[]
  points: number
  state: LibrarySegmentState
  item_id: string | null
}

/** `GET /v1/library/videos/{id}`: the summary (its `segments` count becomes the list of episodes) plus detail. */
export interface LibraryVideoDetail extends Omit<LibraryVideoSummary, 'segments'> {
  environment: string | null
  privacy: { report: Record<string, unknown> | null; decided_by: string | null; decided_at: string | null; note: string | null }
  media: { proxy: boolean; thumbs: boolean; blurred: boolean }
  segments: LibrarySegment[]
}

export interface LibraryVideoQuery {
  status?: LibraryVideoStatus
  review?: LibraryReviewStatus
  privacy?: LibraryPrivacyStatus
  labeled?: boolean
  q?: string
  source_id?: string
  cursor?: string
  limit?: number
}

export interface LibraryVideoPatch {
  title?: string
  notes?: string | null
  review_status?: LibraryReviewStatus
  rate_per_min?: number | null
  environment?: string | null
}

export interface LibrarySegmentPatch {
  start_ms?: number
  end_ms?: number
  title?: string
  environment?: string | null
  status?: LibrarySegmentStatus
  notes?: string | null
}

/** Ego agents' plan item: one library episode submitted for an agent in its slot. */
export interface EgoAgentItem {
  id: string
  agent_id: string
  segment_id: string
  video_id: string
  video_title: string
  title: string
  points: number
  slot_at: string
  status: 'planned' | 'generating' | 'ready' | 'submitted' | 'accepted' | 'rejected' | 'missed' | 'cancelled'
  error: string | null
}

export interface EgoAgentStatus extends AgentDataStatus {
  library: { videos_usable: number; segments_available: number; points_available: number }
}

/** The library media path (realdata service) for a video's file. */
export function libraryMediaPath(videoId: string, name: LibraryMediaName): string {
  return `/v1/library/videos/${encodeURIComponent(videoId)}/media/${name}`
}

// ── Client ───────────────────────────────────────────────────────────────────────────────────────────────────

export function egoApi(c: ApiClient) {
  const rd = <R>(path: string, query?: Record<string, unknown>) => c.get<R>('realdata', path, { query: query as never })
  return {
    me: {
      captures: (q: { status?: string; cursor?: string; limit?: number } = {}) => rd<MyCapturesPage>('/v1/me/captures', q),
      capture: (id: string) => rd<MyCaptureDetail>(`/v1/me/captures/${enc(id)}`),
      summary: () => rd<EgoSummary>('/v1/me/summary'),
      /** null before the first save (the screen starts from `defaultAnnotation`). */
      annotation: async (id: string): Promise<AnnotationState | null> => {
        try {
          return await rd<AnnotationState>(`/v1/captures/${enc(id)}/annotation`)
        } catch (e) {
          if (e instanceof ApiError && e.isNotFound) return null
          throw e
        }
      },
      saveAnnotation: (id: string, body: AnnotationDoc & { base_version: number }) => c.put<AnnotationSaved>('realdata', `/v1/captures/${enc(id)}/annotation`, body),
      /** Send again after labels were not accepted (`needs_correction`); 409 `labels_unchanged` until a label changed. */
      resubmit: (id: string) => c.post<MyCaptureDetail>('realdata', `/v1/me/captures/${enc(id)}/resubmit`),
      /** Done labelling: the labels lock and a reviewer takes it; 422 `needs_labels` with `missing` otherwise. */
      finish: (id: string) => c.post<FinishResult>('realdata', `/v1/me/captures/${enc(id)}/finish`),
      /** Deletes a recording that still needs labels; 409 `not_deletable` after Finish. */
      deleteRecording: (id: string) => c.post<DeleteResult>('realdata', `/v1/me/captures/${enc(id)}/delete`),
      /** Ask for a second look at a recording that wasn't accepted (10 characters at least). */
      appeal: (id: string, message: string) => c.post<MyCaptureDetail>('realdata', `/v1/me/captures/${enc(id)}/appeal`, { message }),
    },
    vocab: {
      bundle: (kinds: readonly string[] = ['environment', 'task', 'skill', 'object_category'], locale = 'en') => c.get<VocabBundle>('platform', '/v1/skills/bundle', { query: { kinds, locale } }),
      search: (q: { q: string; kind?: string; locale?: string; limit?: number }) => c.get<{ items: SkillSearchHit[] }>('platform', '/v1/skills/search', { query: { limit: 20, locale: 'en', ...q } }),
      popular: (q: { kind?: string; environment?: string; limit?: number } = {}) => rd<{ items: { key: string; count: number }[] }>('/v1/labels/popular', q),
    },
    campaigns: {
      /** The active realdata missions (the open one, collection requests, practice) with each request's progress. */
      list: () => rd<{ items: EgoMissionSpec[] }>('/v1/missions'),
      match: (body: CampaignMatchBody) => c.post<{ items: CampaignMatch[] }>('realdata', '/v1/campaigns/match', body),
    },
    review: {
      /** Reviewer reason codes with their shortcut keys and the contributor's text (existing route). */
      reasons: () => rd<{ decisions: { key: string; decision: string }[]; reasons: { code: string; key: string; public_code: string; title: string; body: string; tip?: string }[] }>('/v1/review/reasons'),
      queue: (q: { kind?: 'capture' | 'correction'; overdue?: boolean; cursor?: string; limit?: number } = {}) => rd<{ items: ReviewQueueItem[]; next: string | null }>('/v1/review/queue', q),
      item: async (id: string) => normalizeReviewItem(await rd<Record<string, unknown>>(`/v1/review/items/${enc(id)}`)),
      decide: (id: string, body: ReviewDecisionBody & { next?: boolean }) => c.post<ReviewDecisionResult>('realdata', `/v1/review/items/${enc(id)}/decision`, body),
      proposals: (q: { status?: ProposalStatus; cursor?: string } = {}) => rd<{ items: LabelProposal[]; next?: string | null }>('/v1/review/proposals', q),
      proposal: (id: string) => rd<ProposalDetail>(`/v1/review/proposals/${enc(id)}`),
      decideProposal: (id: string, body: ProposalDecisionBody) => c.post<LabelProposal>('realdata', `/v1/review/proposals/${enc(id)}/decision`, body),
    },
    labels: {
      coverage: (q: { kind?: string; key?: string } = {}) => rd<LabelCoverage>('/v1/labels/coverage', q),
    },
    admin: {
      mission: (id: string) => rd<RealdataAdminMission>(`/v1/admin/missions/${enc(id)}`),
      updateMission: (id: string, body: CampaignPatch) => c.put<RealdataAdminMission>('realdata', `/v1/admin/missions/${enc(id)}`, body),
      progress: (id: string) => rd<CampaignProgress>(`/v1/admin/missions/${enc(id)}/progress`),
      capturePolicy: () => rd<CapturePolicy>('/v1/admin/capture-policy'),
      setCapturePolicy: (body: Partial<CapturePolicy> & { reason: string }) => c.put<CapturePolicy>('realdata', '/v1/admin/capture-policy', body),
      contributor: (userId: string) => rd<ContributorRealdata>(`/v1/admin/contributors/${enc(userId)}`),
    },
    /** Ego library (ops): staff footage from the bucket, cut into labelled episodes (view review:realdata, edit
     *  realdata:operate). */
    library: {
      imports: () => rd<{ items: LibraryImport[] }>('/v1/library/imports'),
      startImport: (body: { source_id: string; prefix?: string }) => c.post<LibraryImport>('realdata', '/v1/library/imports', body),
      sources: () => rd<{ items: LibrarySource[] }>('/v1/library/sources'),
      createSource: (body: { name: string; kind: LibrarySourceKind; config: Record<string, unknown> }) =>
        c.post<LibrarySource>('realdata', '/v1/library/sources', body),
      updateSource: (id: string, body: { name?: string; enabled?: boolean; config?: Record<string, unknown> }) =>
        c.patch<LibrarySource>('realdata', `/v1/library/sources/${enc(id)}`, body),
      deleteSource: (id: string) => c.delete<{ ok: boolean; deleted: boolean; disabled: boolean }>('realdata', `/v1/library/sources/${enc(id)}`),
      checkSource: (id: string) => c.post<LibrarySourceCheck>('realdata', `/v1/library/sources/${enc(id)}/check`),
      videos: (q: LibraryVideoQuery = {}) => rd<{ items: LibraryVideoSummary[]; next: string | null }>('/v1/library/videos', { ...q }),
      video: (id: string) => rd<LibraryVideoDetail>(`/v1/library/videos/${enc(id)}`),
      updateVideo: (id: string, body: LibraryVideoPatch) => c.patch<LibraryVideoDetail>('realdata', `/v1/library/videos/${enc(id)}`, body),
      reprocess: (id: string) => c.post<LibraryVideoDetail>('realdata', `/v1/library/videos/${enc(id)}/reprocess`),
      startPrivacy: (id: string) => c.post<LibraryVideoDetail>('realdata', `/v1/library/videos/${enc(id)}/privacy`),
      decidePrivacy: (id: string, body: { decision: 'approved' | 'rejected' | 'approved_without_blur'; note?: string }) =>
        c.put<LibraryVideoDetail>('realdata', `/v1/library/videos/${enc(id)}/privacy`, body),
      createSegment: (videoId: string, body: { start_ms: number; end_ms: number; title?: string; environment?: string | null }) =>
        c.post<LibrarySegment>('realdata', `/v1/library/videos/${enc(videoId)}/segments`, body),
      updateSegment: (id: string, body: LibrarySegmentPatch) => c.patch<LibrarySegment>('realdata', `/v1/library/segments/${enc(id)}`, body),
      splitSegment: (id: string, at_ms: number) => c.post<{ segments: [LibrarySegment, LibrarySegment] }>('realdata', `/v1/library/segments/${enc(id)}/split`, { at_ms }),
      deleteSegment: (id: string) => c.delete<{ ok: boolean }>('realdata', `/v1/library/segments/${enc(id)}`),
      setLabels: (id: string, labels: readonly LibraryLabel[]) => c.put<LibrarySegment>('realdata', `/v1/library/segments/${enc(id)}/labels`, { labels }),
    },
    /** Ego recording agents' plan (ops, realdata:operate): same shapes as the sim service's agentdata API. */
    agents: {
      status: () => rd<EgoAgentStatus>('/v1/egoagents/status'),
      settings: (body: Partial<AgentDataSettings>) => c.put<EgoAgentStatus>('realdata', '/v1/egoagents/settings', body),
      days: (q: { from: string; to: string }) => rd<{ days: AgentDataDay[] }>('/v1/egoagents/days', q),
      setDay: (day: string, body: { points_budget: number; note?: string }) => c.put<AgentDataDay>('realdata', `/v1/egoagents/days/${enc(day)}`, body),
      replan: (day: string) => c.post<AgentDataDay>('realdata', `/v1/egoagents/days/${enc(day)}/replan`),
      clearDay: (day: string) => c.delete<{ ok: boolean }>('realdata', `/v1/egoagents/days/${enc(day)}`),
      items: (day: string, q: { agent_id?: string } = {}) => rd<{ items: EgoAgentItem[] }>(`/v1/egoagents/days/${enc(day)}/items`, q),
    },
    /** Platform taxonomy edits (§12, `skills:manage`). */
    taxonomy: {
      update: (key: string, body: SkillTaxonomyPatch) => c.put<Record<string, unknown>>('platform', `/v1/skills/${enc(key)}`, body),
      /** Answers with the surviving node and what moved onto it. */
      merge: (key: string, into: string, reason?: string) => c.post<SkillMergeResult>('platform', `/v1/skills/${enc(key)}/merge`, { into, ...(reason ? { reason } : {}) }),
    },
  }
}

export type EgoApi = ReturnType<typeof egoApi>

/** The realdata mission id behind a platform mission (`domain_ref` `realdata:mission:<id>`), or null. */
export function realdataMissionId(domainRef: string | null | undefined): string | null {
  const m = domainRef?.match(/^realdata:mission:(.+)$/)
  return m ? m[1]! : null
}
